import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import useApi from "./useApi";
import { setMfaSetupRequiredHandler } from "../api/apiClient";
import { Role } from "../components/Access/Role";
import { audienceForRoles } from "../components/Access/audience";
import SessionService from "../services/SessionService";
import { setUserTimezone } from "../utils/dateUtils";
import { queryClient } from "../api/queryClient";
import {
  forgetIdentity,
  identityQuery,
  patchIdentity,
  publishIdentity,
} from "../api/identityCache";

// Enum values matching backend
export enum RequiredActionType {
  Dialog = 0,
  Redirect = 1,
  Banner = 2,
  Notification = 3,
}

export enum RequiredActionPriority {
  Critical = 0,
  High = 1,
  Medium = 2,
  Low = 3,
}

export interface RequiredAction {
  id: string;
  actionName: string;
  description?: string;
  actionType: RequiredActionType;
  priority: RequiredActionPriority;
  targetRoles?: string[];
  version?: string;
  expiresAt?: string;
  metadata?: unknown;
}

export interface UserIdentity {
  id?: string;
  // No `email`: GET /identity has never returned one (IdentityModel serves only the caller's name,
  // roles and photo). It was declared here anyway, so the no-roles card rendered "Your account ()".
  firstName?: string;
  lastName?: string;
  isRegistered?: boolean;
  roles?: string[];
  profilePictureUrl?: string;
  timeZone?: string;
  requiredActions?: RequiredAction[];
  mfaSetupRequired?: boolean;
  /** TeleHeadache Review & Sign is unfinished: the patient is held on the enrollment screen. */
  enrollmentRequired?: boolean;
  /** Review & Sign applies to this patient (and recorded their Terms acceptance). */
  enrollmentApplies?: boolean;
  /** CONS-4: yyyy-MM-dd when revised documents await this patient's acceptance, else null. */
  documentUpdatesDueBy?: string | null;
  /** CONS-3: the patient withdrew consent to treatment; new care requests are refused. */
  clinicalConsentWithdrawn?: boolean;
  /** True only when this deployment can actually TEXT a sign-in code (MFA on AND SMS on). */
  smsMfaAvailable?: boolean;
  /** True when this deployment texts notifications and reminders too (SMS-4/SMS-5). */
  smsNotificationsAvailable?: boolean;
}

export interface CerberusContextValue {
  userId: string | undefined;
  cerberusUser: UserIdentity;
  isRegistered: boolean;
  mfaSetupRequired: boolean;
  enrollmentRequired: boolean;
  smsMfaAvailable: boolean;
  notificationCount: number;
  unreadMessagesCount: number;
  loading: boolean;
  roles: string[];
  requiredActions: RequiredAction[];
  updateNotificationCount: (count?: number) => void;
  updateUnreadMessagesCount: (count?: number) => void;
  reloadIdentity: () => void;
  userHasRole: (roleName: Role) => boolean;
  userHasOneOfRoles: (roleNames: Role[]) => boolean;
  userHasClinicalRoles: () => boolean;
  markUserAsRegistered: () => void;
  refreshUserData: () => Promise<void>;
  completeRequiredAction: (
    actionId: string,
    actionVersion?: string,
    completionData?: unknown,
  ) => Promise<boolean>;
  getCriticalActions: () => RequiredAction[];
  getNonCriticalActions: () => RequiredAction[];
}

export const CerberusContext = React.createContext<CerberusContextValue | null>(null);
export const useCerberus = (): CerberusContextValue => {
  const context = useContext(CerberusContext);
  if (!context) throw new Error("useCerberus must be used within CerberusProvider");
  return context;
};
export const CerberusProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [isRegistered, setIsRegistered] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [enrollmentRequired, setEnrollmentRequired] = useState(false);
  const [smsMfaAvailable, setSmsMfaAvailable] = useState(false);
  const [cerberusUser, setCerberusUser] = useState<UserIdentity>({});
  const [userId, setUserId] = useState<string>();
  const [roles, setRoles] = useState<string[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [requiredActions, setRequiredActions] = useState<RequiredAction[]>([]);
  const sessionService = SessionService.getInstance();

  const api = useApi();

  /**
   * Read identity into this provider's state.
   *
   * The request itself belongs to the query cache (`identityQuery`), not to this effect. The root
   * route loader asks for the same key before the first render, so by the time this runs the answer
   * is usually already there or already in flight — one `/identity` call either way, and the shell
   * no longer waits for a request that starts after the first paint.
   *
   * `force` is for the moments when the cached copy is the *old* answer and re-reading is the whole
   * point: a sign-in, an accepted invitation, a verified MFA phone. Everything else can have the
   * cached one.
   */
  const fetchUserData = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      setLoading(true);
      if (sessionService.isAuthenticated()) {
        try {
          if (force) forgetIdentity(queryClient);
          const cerberusUser = force
            ? await queryClient.fetchQuery({ ...identityQuery, staleTime: 0 })
            : await queryClient.ensureQueryData(identityQuery);
          setCerberusUser(cerberusUser);
          setUserId(cerberusUser.id);
          setIsRegistered(!!cerberusUser.isRegistered);
          setMfaSetupRequired(!!cerberusUser.mfaSetupRequired);
          setEnrollmentRequired(!!cerberusUser.enrollmentRequired);
          setSmsMfaAvailable(!!cerberusUser.smsMfaAvailable);
          setRoles(cerberusUser.roles ?? []);
          setRequiredActions(cerberusUser.requiredActions || []);

          // Cache the user's timezone in localStorage if it exists
          if (cerberusUser.timeZone) {
            setUserTimezone(cerberusUser.timeZone);
          }
        } catch (error) {
          console.error("Failed to fetch user data:", error);
          // Drop the failed entry rather than leaving it cached: a loader reading identity next
          // would otherwise see the error state instead of asking again.
          forgetIdentity(queryClient);
          sessionService.clearSession();
        }
      } else {
        // Not authenticated — don't call /identity since it requires a session cookie
        setCerberusUser({});
        setUserId(undefined);
        setIsRegistered(false);
        setMfaSetupRequired(false);
        setRoles([]);
        setRequiredActions([]);
        publishIdentity(queryClient, null);
      }
      setLoading(false);
    },
    [sessionService],
  );

  const getNotificationsCount = useCallback(async () => {
    // Only call if the user is authenticated, registered and holds a role: the server refuses an
    // account with no roles everything but identity and session, so this would only raise a 403.
    // Caregivers are refused this endpoint (no [AllowCaregiver]), so their shell never asks. Nor
    // does an account still owing MFA setup: the gate 403s it, and that 403 triggers an identity
    // re-read whose fresh `roles` array re-fires the counts effect — an endless loop (2026-10-20).
    if (
      sessionService.isAuthenticated() &&
      isRegistered &&
      !mfaSetupRequired &&
      roles.length > 0 &&
      audienceForRoles(roles) !== "caregiver"
    ) {
      try {
        const result = await api.get(`/notifications/uncleared-count`);
        setNotificationCount(result.data);
      } catch (error) {
        console.log("Unable to fetch notifications count:", error);
      }
    }
  }, [api, sessionService, isRegistered, mfaSetupRequired, roles]);

  const getUnreadMessagesCount = useCallback(async () => {
    // Only call if the user is authenticated, registered and holds a role: the server refuses an
    // account with no roles everything but identity and session, so this would only raise a 403.
    // Caregivers are refused this endpoint (no [AllowCaregiver]), so their shell never asks. Same
    // MFA-setup rule as getNotificationsCount.
    if (
      sessionService.isAuthenticated() &&
      isRegistered &&
      !mfaSetupRequired &&
      roles.length > 0 &&
      audienceForRoles(roles) !== "caregiver"
    ) {
      try {
        const result = await api.get(`/messages/unread-count`);
        setUnreadMessagesCount(result.data);
      } catch (error) {
        console.log("Unable to fetch unread messages count:", error);
      }
    }
  }, [api, sessionService, isRegistered, mfaSetupRequired, roles]);

  const completeRequiredAction = async (
    actionId: string,
    actionVersion?: string,
    completionData?: unknown,
  ) => {
    try {
      await api.post("/required-actions/complete", {
        actionId,
        actionVersion,
        completionData,
      });

      // Remove the completed action from the list, in state and in the cached identity both.
      setRequiredActions((prev) =>
        prev.filter((action) => action.id !== actionId),
      );
      patchIdentity(queryClient, (identity) => ({
        requiredActions: (identity.requiredActions ?? []).filter(
          (action) => action.id !== actionId,
        ),
      }));

      return true;
    } catch (error) {
      console.error("Failed to complete required action:", error);
      return false;
    }
  };

  const getCriticalActions = () => {
    return requiredActions.filter(
      (action) => action.priority === RequiredActionPriority.Critical,
    );
  };

  const getNonCriticalActions = () => {
    return requiredActions.filter(
      (action) => action.priority !== RequiredActionPriority.Critical,
    );
  };

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Bridge for the server-side MFA gate (AUD-15). The API 403s with code `mfa_setup_required` when
  // the account has not verified a phone; re-reading /identity (which is outside the gate) flips
  // `mfaSetupRequired` and RootLayout swaps in the setup screen. Guarded so a page that fires five
  // requests at once triggers one refresh, not five.
  //
  // Also a no-op once the flag is already set: the setup screen is showing, so a stray 403 has
  // nothing to tell us, and re-reading identity blanks the screen (loading) and remounts it.
  const mfaRefreshInFlight = useRef(false);
  const mfaSetupRequiredRef = useRef(mfaSetupRequired);
  mfaSetupRequiredRef.current = mfaSetupRequired;
  useEffect(() => {
    setMfaSetupRequiredHandler(() => {
      if (mfaRefreshInFlight.current || mfaSetupRequiredRef.current) return;
      mfaRefreshInFlight.current = true;
      fetchUserData({ force: true }).finally(() => {
        mfaRefreshInFlight.current = false;
      });
    });
  }, [fetchUserData]);

  // Separate effect to fetch counts after user data is loaded
  useEffect(() => {
    // After user data is fetched, get notification and message counts
    // Only if the user is signed in AND registered.
    if (sessionService.isAuthenticated() && isRegistered && userId && roles.length > 0) {
      getNotificationsCount();
      getUnreadMessagesCount();
    }
  }, [
    userId,
    isRegistered,
    sessionService,
    roles.length,
    getNotificationsCount,
    getUnreadMessagesCount,
  ]);

  return (
    <CerberusContext.Provider
      value={{
        userId,
        cerberusUser,
        isRegistered,
        mfaSetupRequired,
        enrollmentRequired,
        smsMfaAvailable,
        notificationCount,
        unreadMessagesCount,
        loading,
        roles,
        updateNotificationCount: (count?: number) => {
          if (count !== undefined) {
            setNotificationCount(count);
          } else {
            getNotificationsCount();
          }
        },
        updateUnreadMessagesCount: (count?: number) => {
          if (count !== undefined) {
            setUnreadMessagesCount(count);
          } else {
            getUnreadMessagesCount();
          }
        },
        reloadIdentity: () => {
          fetchUserData({ force: true });
        },
        userHasRole: (roleName: Role) => {
          return roles.includes(roleName);
        },
        userHasOneOfRoles: (roleNames: Role[]) => {
          let hasRole = false;

          roleNames.forEach((role) => {
            if (roles.includes(role)) {
              hasRole = true;
            }
          });

          return hasRole;
        },
        userHasClinicalRoles: () => {
          const clinicalRoles = [
            Role.Provider,
            Role.Nurse,
            Role.MedicalAssistant,
            Role.MtcDoc,
            Role.Admin,
            Role.Developer,
          ];

          return clinicalRoles.some((role) => roles.includes(role));
        },
        markUserAsRegistered: () => {
          setIsRegistered(true);
          patchIdentity(queryClient, { isRegistered: true });
        },
        // Callers reach for this after something that changes who the user is — a sign-in, an
        // accepted invitation — so it always goes back to the server.
        refreshUserData: () => fetchUserData({ force: true }),
        requiredActions,
        completeRequiredAction,
        getCriticalActions,
        getNonCriticalActions,
      }}
    >
      {children}
    </CerberusContext.Provider>
  );
};
